import { useState, useEffect, useRef } from "react";
import { Dialog, DialogTitle, DialogContent, Button, Grid } from "@mui/material";
import { OTRModuleModel, PartnerDataType } from "../models/OTRModuleModel";
import { compactString, convertDateFromXlsx, formatUtcToLocal, isEmptyOrUndefined, isNanOrZero, readXlsxFile, writeXlsxFile } from "../helper/utils";
import { clientList, columnList, excelExportTypes, similarList, taxTypeList, transactionTypeList } from "./OpenTaxReport/constants";
import moment from "moment";
import { CorrectedformatCurrency } from "../helper/currencyFormatter";
import { apiClient } from "../helper/api";
import useAuth from "../context/AuthProvider";
import { Navigate, useNavigate } from "react-router-dom";
import ImportStatusDialog from "../components/Elements/ImportStatusPopup";
import { AppRoutes } from "../helper/appRoutes";

export const CustomerOpenTaxReport = () => {
  const { user } = useAuth()
  const token = user?.token
  const [loading, setloading] = useState(false)
  const [status, setstatus] = useState(false);
  const [openImportStatusDialog, setOpenImportStatusDialog] = useState(false);
  const isImportCompleted = useRef<string>("");

  async function checkProcessStatus() {
    let tempStatus = "In Progress";
    const intervalTime = 5000; // 5 seconds
    const maxTime = 40000; // 30 seconds
    const startTime = Date.now();

    const intervalId = setInterval(async () => {
      try {
        if (Date.now() - startTime >= maxTime) {
          // console.log("Process timeout after 30 seconds.");
          await new Promise(resolve => setTimeout(resolve, 3000));
          isImportCompleted.current = "Timeout";
          tempStatus = "Timeout";
          clearInterval(intervalId);
          return; // Exit the function early to avoid further processing
        }

        if (isImportCompleted.current === "In Progress") {
          CheckImportStatus();
        }
        if (isImportCompleted.current === "Completed" || isImportCompleted.current === "Not Importing") {
          // console.log("Process completed.");
          isImportCompleted.current = "Completed";
          tempStatus = "Completed";
          await new Promise(resolve => setTimeout(resolve, 2000));
          handleCloseImportStatusDialog();
          clearInterval(intervalId);
        }
        if (isImportCompleted.current === "") {
          // console.log("Cleared");
          clearInterval(intervalId);
        }
      } catch (error) {
        console.error("Error fetching process status:", error);
      }
    }, intervalTime);
  }

  const updateOTRData = async (items: OTRModuleModel[], action: number, file: File | null = null) => {
    const formData = new FormData();
    formData.append('openTaxReport', JSON.stringify(items));
    if (file) {
      formData.append('missingFile', file);
    }
    setloading(true)
    const res = await apiClient(`/OpenTaxReport/SaveOrUpdate`, {
      method: "POST",
      headers: new Headers({
        // 'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      }),
      // body: JSON.stringify(items),
      body: formData
    }).finally(() => {
      setloading(false)
    })
    if (!res?.data) return

    if (action === 1) {
      // CheckImportStatus();
      isImportCompleted.current = "In Progress";

      checkProcessStatus();
      setOpenImportStatusDialog(true);
    }
  }

  const partnerDataDetails: PartnerDataType = {
    customerName: "",
    partnersId: 0
  }
  const [excelFormatList, setExcelFormatList] = useState<PartnerDataType[]>([partnerDataDetails])
  const fetchCustomer = async () => {
    return apiClient("/Partners/getPartners", {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    })
      .then((responseJson) => {
        if (!responseJson) return
        // Do something with the response
        const update = responseJson.data.map((item: any) => item.customerName)
        setExcelFormatList(responseJson.data);
        return update
      })
  };

  const CheckImportStatus = () => {
    apiClient(`/OpenTaxReport/CheckImportStatus`, {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    })
      .then((responseJson) => {
        if (responseJson?.data != null) {
          isImportCompleted.current = responseJson.data;
        }
      })
  };

  const handleCloseImportStatusDialog = () => {
    handleClosePopup()
    setOpenImportStatusDialog(false);
    isImportCompleted.current = "";
  };

  useEffect(() => {
    fetchCustomer()
  }, []);

  // List of fields that need currency formatting
  const currencyFields = ['taxAmt', 'grossPremium', 'taxableFees', 'taxBalance', 'nonAdmittedPremium', 'nonTaxableFees'];
  const dateInvoiceAddedField = 'dateInvoiceAdded';
  const exportXlsx = async (items: OTRModuleModel[], excelExportType: number) => {
    if (items.length === 0) {
      return
    }
    const excelItem = excelExportTypes.find(x => x.excelType === excelExportType)
    if (!excelItem) return

    const updates1: { [x: string]: any[] } = {}
    // excelItems.forEach(ex => {
    const headerRow = excelItem.columnOrder.map(idx => columnList[idx].xlsHeader[0])
    updates1[excelItem.name] = [headerRow]
    // })

    items.forEach(item => {
      const xlsHeaders: string[] = []
      Object.keys(item).map((field) => {
        if (!item[field as keyof OTRModuleModel]) return
        const column = columnList.find(col => col.field === field)
        if (!column?.xlsHeader) return
        xlsHeaders.push(column.xlsHeader[0])
      })
      // const excelItem = determineExcel(xlsHeaders)
      const update1 = excelItem.columnOrder.map(idx => {
        const column = columnList[idx]
        const field = column.field as keyof OTRModuleModel
        const value = item[field]
        if (value === undefined || value === null) return
        switch (column.inputType) {
          case 'string':
            if (field === dateInvoiceAddedField) {
              return formatUtcToLocal(value as string, 'MM-DD-YYYY HH:mm');
            }
            return value.toString()
          case 'date':
            return value ? formatUtcToLocal(value as string, 'MM-DD-YYYY HH:mm') : undefined;
          case 'number':
            if (currencyFields.includes(field as string)) {
              return CorrectedformatCurrency(value.toString());
            }
            return +value;
          case 'bool':
            return !!value
          default:
            return value
        }
      })
      updates1[excelItem.name].push(update1)
    })

    if (!updates1[excelItem.name] || updates1[excelItem.name].length <= 1) return
    const timeStamp = moment().format('YYYY-M-D')
    return writeXlsxFile(updates1[excelItem.name], `${timeStamp}_${excelItem.name}.xlsx`)
  }

  const isXlsxHeaderExist = (xlsHeaders: string[], fieldName: string) => {
    const columns = columnList.filter(col => col.field === fieldName)
    if (!columns.length) return false
    const possibleHeaders = (columns.map(c => c.xlsHeader)).flat(1)
    const columnHeader = possibleHeaders.map(compactString).map(compactString)
    const headerIndex = xlsHeaders.findIndex((header: string) => columnHeader.includes(compactString(header)))
    return headerIndex > -1
  }


  const determineColumnFromList = (header: string) => {
    const column = columnList.find(column => {
      const headerKey = compactString(column.xlsHeader[0])
      const headerCompact = compactString(header)
      if (headerKey && headerCompact) {
        return column.xlsHeader.map(compactString).includes(compactString(headerCompact))
      }
      return false
    })
    return column
  }

  const onImportXlsx = async (file: File, client: string) => {
    setloading(true)
    if (status == false) {
      setstatus(true)
    }
    const sheetsData = await readXlsxFile(file)
    if (!sheetsData || Object.keys(sheetsData).every((sheetName: string) => !sheetsData[sheetName] || sheetsData[sheetName].length <= 1)) {
      alert("The file is empty or invalid. Please check the file.");
      setloading(false)
      return;
    }

    const missingHeaderList: string[] = []
    const updateList: OTRModuleModel[] = []

    let missingUpdates: { [key: string]: OTRModuleModel[] } = {
      client_Required: [],
      grossPremium_Required: [],
      SLTax_StampFee_0: [],
      TaxAmt_TaxBase_Required: [],
    }

    for (const sheetName in sheetsData) {
      const sheet = sheetsData[sheetName]
      const xlsHeaders = sheet[0]
      if (!xlsHeaders || xlsHeaders.length < 1) continue
      // _rowNum is a number matched with excel file's row number. (debug purpose)
      xlsHeaders[0] = '_rowNum'

      const content = sheet.slice(1).filter((item: any) => {
        if (!item || item.length <= 1) return false
        return true
      })
      console.log('excel items:', content.length, { content })
      // const excelItem = determineExcel(xlsHeaders)
      let updates: OTRModuleModel[] = []
      xlsHeaders.forEach((header: string, idx: number) => {
        const column = determineColumnFromList(header)
        if (!column && header) {
          missingHeaderList.push(header);
        }
      });

      content.forEach((item: any, index: number) => {
        const isValidItem = item.filter((val: any) => val !== undefined || val === '').length > 5
        if (!isValidItem) return
        var isReturn = 0;

        const update: OTRModuleModel = {}

        update.NewOpenTaxReportId = 0
        xlsHeaders.forEach((header: string, idx: number) => {
          const column = determineColumnFromList(header)
          if (!column) {
            // Ignored when excel header name is not registered (missingHeaders)
            return
          }

          if (column.field === 'taxType') {
            if (!item[idx]) {
              return;
            }
            item[idx] = item[idx]?.split('-').at(-1)?.trim();
            if (item[idx].trim().toLowerCase() === 'state tax') {
              item[idx] = 'Surplus Lines Tax';
            }
          }
          const field = column.field as keyof OTRModuleModel
          switch (column.inputType) {
            case 'string':
              update[field] = item[idx]
              if (similarList[field]) {
                const itemValueStr = item[idx] != null ? String(item[idx]) : '';
                const scen = similarList[field].find(scen => scen.possibleValues.map(compactString).includes(compactString(itemValueStr)))
                update[field] = (scen?.value ?? itemValueStr) as any
              } else {
                update[field] = item[idx]?.toString()
              }
              break;

            case 'date':
              const dVal = new Date(item[idx]).valueOf();
              if (!item[idx]) break;

              let dateString: string | undefined = '';
              if (dVal > new Date('1990/1/1').valueOf() && dVal < new Date('2990/1/1').valueOf()) {
                // Keeping the date in local time without converting to UTC
                const date = new Date(dVal);
                dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T00:00:00`; // Format manually in local time
              } else {
                const parsedDate = convertDateFromXlsx(dVal);
                const date = parsedDate ? new Date(parsedDate) : undefined;
                dateString = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T00:00:00` : undefined; // Format manually
              }

              update[field] = dateString as any;
              break;
            case 'number':
              const nVal = (item[idx] === "" || item[idx] === undefined) ? undefined : (+item[idx])?.toFixed(2).replace(/\.?0+$/, '')
              update[field] = nVal as any
              break;
            case 'bool':
              update[field] = (!!item[idx]) as any
              break;
            default:
              update[field] = item[idx]?.toString()
              break;
          }
        })

        // update['filerNotes'] = ''
        update['dateInvoiceAdded'] = new Date().toISOString()

        // If "transEffDate" header not exist, update it from "effDate"
        if (!isXlsxHeaderExist(xlsHeaders, 'transEffDate') && isXlsxHeaderExist(xlsHeaders, 'effDate')) {
          update['transEffDate'] = update['effDate']
        }
        // If "policyEffDate" header not exist, update it from "effDate"
        if (!isXlsxHeaderExist(xlsHeaders, 'policyEffDate') && isXlsxHeaderExist(xlsHeaders, 'effDate')) {
          update['policyEffDate'] = update['effDate']
        }
        // If "client" header not exist, update value
        if (isEmptyOrUndefined(update['client'])) {
          update['client'] = client
        } else {
          const matchedClient = clientList.find(c => c.possibleValues.some(val => compactString(val) === compactString(update['client'])))
          if (matchedClient) {
            update['client'] = matchedClient.value
          }
          // Keep full client name for display in missing file when client is incorrect
          const originalClientName = (update['client'] ?? '').toString().trim();
          const clientForLookup = originalClientName.length > 10
            ? originalClientName.slice(0, -6).trim()
            : originalClientName;
          const isExistClient = excelFormatList.findIndex(c =>
            c.customerName.toLowerCase().includes(clientForLookup.toLowerCase())
          );
          if (isExistClient == -1) {
            missingUpdates.client_Required.push({ ...update, client: originalClientName, incorrectField: 'client is incorrect' })
            // return
            isReturn++;
          } else {
            update['client'] = excelFormatList[isExistClient].customerName;
          }
        }

        const singlePartnerData = excelFormatList.find((p: PartnerDataType) => p.customerName === client);
        update['partnersId'] = singlePartnerData?.partnersId;

        //If "grossPremium" is null or 0, ignore update
        if (isNanOrZero(update['grossPremium'])) {
          missingUpdates.grossPremium_Required.push({ ...update, incorrectField: 'grossPremium is required' })
          isReturn++;
        }

        if (isXlsxHeaderExist(xlsHeaders, 'taxSL') && isXlsxHeaderExist(xlsHeaders, 'stampFee')) {
          // If "taxSL" and "stampFee" fields exists
          if (!isNanOrZero(update['taxSL'])) {
            update['taxType'] = 'Surplus Lines Tax';
          }

          if (!isNanOrZero(update['stampFee'])) {
            update['taxType'] = 'Stamping Fee';
          }
          // Ignore when taxSL or stampFee = 0
          if ((isNanOrZero(update['taxSL'])) || (isNanOrZero(update['stampFee']))) {
            missingUpdates.SLTax_StampFee_0.push({ ...update, incorrectField: `"S/L Tax" or "Stamp Fee" should not be 0` })
            isReturn++;
          }
        }
        else if (isXlsxHeaderExist(xlsHeaders, 'taxAmt') && !isNanOrZero(update['taxAmt'])) {
          if (isReturn > 0) {
            return
          }
          // If "taxAmt" field exists
          if (isReturn > 0) {
            return
          }
          updates = [...updates, update]
          return
        } else {
          // Ignore when taxAmt = 0
          missingUpdates.TaxAmt_TaxBase_Required.push({ ...update, incorrectField: `"Tax Amount" or "TaxAmt" or "Tax Base" is required` })
          isReturn++;
        }
      })

      const missingCounts = Object.values(missingUpdates).reduce((sum, arr) => sum + arr.length, 0)
      console.log('missing items:', missingCounts, { missingUpdates })

      let index = 0;
      while (index < updates.length) {
        const element = updates[index];
        const transactionType = element['transactionType'];
        // Convert to string to handle both numbers and strings
        const transactionTypeStr = transactionType != null ? String(transactionType) : '';
        if (transactionTypeStr && transactionTypeStr.toLowerCase().includes('endorsement')) {
          updates[index].transactionType = 'Endorsement';
        } else {
          // If transaction type matches the list, use the matched standard value
          const matchedTransType = transactionTypeList.find(tr => 
            tr.possibleValues.some(pv => compactString(pv) === compactString(transactionTypeStr)) || 
            compactString(tr.value) === compactString(transactionTypeStr)
          );
          if (matchedTransType) {
            updates[index].transactionType = matchedTransType.value;
          }
          // If no match, keep the original transactionType from Excel (will be highlighted red in UI)
        }
        if (compactString(element['carrier']) === 'lloyd' || element['carrier'] === "Lloyd's") {
          updates[index].carrier = "Certain Underwriters at Lloyd's of London";
        }

        //Case for Venbrook Insurance Services: when client name came  as VA Changing it to Venbrook Insurance Services
        const client = element['client'];
        if (client === 'VA') {
          updates[index].client = 'Venbrook Insurance Services';
        }

        if (client === 'Cardigan General Insurance Services' || client === 'Cardigan General Insurance Services, LLC') {
          const insuredName = element['insuredName'];
          if (insuredName !== undefined && insuredName.toLowerCase().includes('record(s)')) {
            updates.splice(index, 1);
            continue;
          }
        }

        const taxType = element['taxType'];
        const matchedTaxType = taxTypeList.find(tax => tax.possibleValues.some(value => compactString(value) === compactString(taxType)));
        if (matchedTaxType) {
          updates[index].taxType = matchedTaxType.value;
        }

        const invoiceNumber = element['invoiceNumber'];
        index++;
      }
      updateList.push(...updates)
    }

    const uniqueArray = updateList;
    const missingRecords = [
      ...missingUpdates.client_Required,
      ...missingUpdates.grossPremium_Required,
      ...missingUpdates.SLTax_StampFee_0,
      ...missingUpdates.TaxAmt_TaxBase_Required,
    ]
    if (missingRecords.length > 0) {
      // Export as 'Incorrect Imports'
      const file = await exportXlsx(missingRecords, excelExportTypes[1].excelType)
      await updateOTRData(uniqueArray, 1, file);
    } else {
      await updateOTRData(uniqueArray, 1);
    }
    setloading(false)
  }

  const [openPopup, setOpenPopup] = useState(false);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  
  // Open popup when the component mounts
  useEffect(() => {
    setOpenPopup(true);
  }, []);

  // Close the upload popup and show success message
  const handleClosePopup = () => {
    setOpenPopup(false);
    setSelectedFile(null);
    setTimeout(() => {
      navigate(AppRoutes.customer);
    }, 300);
  };
  const navigate = useNavigate();
  const handleImportXlsx = (file: any, xlsItem: string) => {
    if (!file) {
      alert("Please Upload a file");
      return;
    }
    onImportXlsx(file, xlsItem)
  };
  // Handle file selection
  const handleFileChange = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  return (
    <Dialog
      open={openPopup}
      onClose={handleClosePopup}
      fullWidth
      maxWidth={false}
      sx={{
        "& .MuiDialog-paper": {
          width: "45%", // Set a fixed width for better alignment
          maxWidth: "100%", // Ensure responsiveness
          margin: "0 auto", // Center horizontally
          borderRadius: "12px", // More rounded corners
          padding: "40px 20px", // Increased padding for better spacing
          textAlign: "center", // Center text
          height: "40%", // Adjusted height
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      <DialogTitle
        sx={{ textAlign: "center", fontWeight: "bold", fontSize: "20px", marginBottom: "4%" }}
      >
        Upload Open Tax Report Documents
      </DialogTitle>
      <DialogContent sx={{ padding: "20px 30px" }}>
        <Grid container direction="column" spacing={4} alignItems="center">
          {/* File Upload Box */}
          <Grid
            item
            sx={{ width: "100%", cursor: "pointer" }}
            onClick={() => document.getElementById("uploadXlsxOTR")?.click()}
          >
            <div className="document-item">
              <div
                style={{
                  display: "-webkit-inline-box",
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "2%", // Increased padding
                  borderWidth: 1,
                  borderRadius: 8,
                  borderColor: "#ccc",
                  borderStyle: "dashed",
                  backgroundColor: "rgba(22, 46, 60, 0.10)",
                  color: "#555",
                  outline: "none",
                  transition: "border .24s ease-in-out",
                  textAlign: "center",
                  cursor: "pointer",
                  width: "100%",
                  height: "55px", // Slightly increased height
                }}
              >
                <label style={{ fontSize: "16px", fontWeight: "600" }}>
                  {selectedFile ? selectedFile.name : "Select File..."}
                </label>
                <input
                  id="uploadXlsxOTR"
                  type="file"
                  accept=".xls, .xlsx, .csv"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
              </div>
            </div>
          </Grid>

          {/* Upload and Cancel Buttons */}
          <Grid
            item
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "20px",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                handleImportXlsx(selectedFile, user?.partnerName ?? "")
              }
              disabled={!selectedFile}
              sx={{
                backgroundColor: selectedFile ? "#007bff" : "#b0c4de",
                color: "white",
                padding: "10px 24px",
                borderRadius: "25px",
                fontSize: "14px",
                fontWeight: "bold",
                marginRight: "10px",
                boxShadow: "none",
              }}
            >
              Upload
            </Button>
            <Button
              variant="outlined"
              onClick={handleClosePopup}
              sx={{
                border: "2px solid #007bff",
                color: "#007bff",
                padding: "10px 24px",
                borderRadius: "25px",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              Cancel
            </Button>
          </Grid>
        </Grid>
        <ImportStatusDialog
            open={openImportStatusDialog}
            onClose={handleCloseImportStatusDialog}
            isImportCompletedRef={isImportCompleted} />
      </DialogContent>
    </Dialog>
  );
};
